import { u } from 'unist-builder'
import { all as remarkAll } from 'remark-rehype'
import { ElementContent } from 'hast'
import { table } from 'mdast-util-to-hast/lib/handlers/table'

interface MarkdownItem {
  name: string
  regExp: RegExp
  mdValue: string
}

export enum StyleType {
  HEADER_ONE = 'HEADER_ONE',
  HEADER_TWO = 'HEADER_TWO',
  HEADER_THREE = 'HEADER_THREE',
  HEADER_FOUR = 'HEADER_FOUR',
  HEADER_FIVE = 'HEADER_FIVE',
  HEADER_SIX = 'HEADER_SIX',
  BOLD = 'BOLD',
  ITALIC = 'ITALIC',
  STRIKETHROUGH = 'STRIKETHROUGH',
  CODE = 'CODE',
  UNORDERED_LIST_ITEM = 'UNORDERED_LIST_ITEM',
  ORDERED_LIST_ITEM = 'ORDERED_LIST_ITEM',
  CODE_BLOCK = 'CODE_BLOCK',
  BLOCKQUOTE = 'BLOCKQUOTE',
  HORIZONTAL_RULE = 'HORIZONTAL_RULE',
  TASK_LIST = 'TASK_LIST',
  TABLE = 'TABLE',
  FILE = 'FILE',
}

const BlockquoteStyleItem: MarkdownItem = {
  name: StyleType.BLOCKQUOTE,
  regExp: /> /,
  mdValue: '> ',
}
const OrderedListStyleItem: MarkdownItem = {
  name: StyleType.ORDERED_LIST_ITEM,
  regExp: /[0-9]+\. /,
  mdValue: '0. ',
}
const TaskListStyleItem: MarkdownItem = {
  name: StyleType.TASK_LIST,
  regExp: /- \[.\] /,
  mdValue: '- [ ] ',
}
const UnorderedListStyleItem: MarkdownItem = {
  name: StyleType.UNORDERED_LIST_ITEM,
  regExp: /- /,
  mdValue: '- ',
}
const CodeBlockStyleItem: MarkdownItem = {
  name: StyleType.CODE_BLOCK,
  regExp: /```/,
  mdValue: '```',
}
const TableStyleItem: MarkdownItem = {
  name: StyleType.TABLE,
  regExp: /\|(.*)\|/,
  mdValue: '|',
}

const LineItems: MarkdownItem[] = [BlockquoteStyleItem, OrderedListStyleItem]

export const listItemHandler = (h, node, parent) => {
  const props: {
    [PropertyName: string]:
      | boolean
      | number
      | string
      | null
      | undefined
      | Array<string | number>
  } = {}
  const result = remarkAll(h, node)
  if (typeof node.checked === 'boolean') {
    let p
    if (
      result[0] &&
      result[0].type === 'element' &&
      result[0].tagName === 'p'
    ) {
      p = result[0]
    } else {
      p = h(null, 'p', [])
      result.unshift(p)
    }
    if (p.children.length > 0) {
      p.children.unshift(u('text', ' '))
    }
    p.children.unshift(
      h(null, 'input', {
        type: 'checkbox',
        checked: node.checked,
        disabled: true,
      })
    )
    props.className = ['task-list-item']
  }
  const wrapped: Array<ElementContent> = []
  let index = -1
  while (++index < result.length) {
    const child = result[index]
    if (index !== 0 || child.type !== 'element' || child.tagName !== 'p') {
      wrapped.push(u('text', '\n'))
    }
    if (child.type === 'element' && child.tagName === 'p') {
      let isOutput = true
      if (child.children[0]) {
        const childValue: any = child.children[0]
        const checkValue = childValue.value?.match(/\[(.)\]/)
        if (checkValue) {
          props.className = ['task-list-item']
          wrapped.push(
            h(null, 'input', {
              type: 'checkbox',
              checked: checkValue[1] === 'x',
              disabled: true,
            })
          )
          isOutput = false
        }
      }
      if (isOutput) {
        wrapped.push(...child.children)
      }
    } else {
      wrapped.push(child)
    }
  }
  const tail = result[result.length - 1]
  if (tail && (!('tagName' in tail) || tail.tagName !== 'p')) {
    wrapped.push(u('text', '\n'))
  }
  return h(node, 'li', props, wrapped)
}

export const tableHandler = (h, node, isPreview: boolean) => {
  const tbl: any = table(h, node)
  tbl?.children.map(tableChild => {
    if (tableChild.type !== 'element' || !tableChild.children) return
    tableChild.children.map(rowChild => {
      if (rowChild.type !== 'element' || !rowChild.children) return
      rowChild.children.map(cellChild => {
        if (cellChild.type !== 'element') return
        if (cellChild.children.length === 0) {
          if (isPreview) cellChild.children.push(h(node, 'br'))
        } else {
          replaceBreak(h, node, cellChild)
        }
      })
    })
  })
  return tbl
}

const replaceBreak = (h, node, child) => {
  let index = -1
  const wrapped: Array<ElementContent> = []
  while (++index < child.children.length) {
    const element = child.children[index]
    if (element.type === 'raw' && /^<br\s*\/?>$/i.test(element.value)) {
      wrapped.push(h(node, 'br'))
    } else if (element.type !== 'text') {
      wrapped.push(element)
    } else {
      const replaceValues = element.value.split('  ')
      let subIndex = -1
      while (++subIndex < replaceValues.length) {
        wrapped.push(u('text', replaceValues[subIndex]))
        if (subIndex !== replaceValues.length - 1) {
          wrapped.push(h(node, 'br'))
        }
      }
    }
  }
  child.children = wrapped
}

export const escapeBreakText = (value: string, escapeValue: string): string => {
  let text = ''
  let index = -1
  let isCodeStart = false
  let isJustBeforeStyle = false
  const splitValue = value.split('\n')
  const checkList = [
    ...LineItems,
    TaskListStyleItem,
    UnorderedListStyleItem,
    TableStyleItem,
  ]
  while (++index < splitValue.length) {
    let newValue = splitValue[index]
    if (newValue.match(CodeBlockStyleItem.regExp)) isCodeStart = !isCodeStart
    if (0 < index && !isCodeStart) {
      isJustBeforeStyle = false
      let subIndex = -1
      const checkValue = splitValue[index - 1]
      while (++subIndex < checkList.length) {
        const item = checkList[subIndex]
        if (checkValue.match(item.regExp)) {
          isJustBeforeStyle = true
          break
        }
      }
    }
    if (!isCodeStart && !isJustBeforeStyle && newValue.trim().length === 0) {
      newValue = escapeValue
    }
    text = text + newValue + '\n'
  }
  return text
}
